import React, { useContext, useEffect, useRef, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { loadNuanceScript } from "../../utils/Nuance";
import { BEHAVIORAL_CARE_SOLUTIONS_ID } from "app/constants/practicesIds";

const NuanceContext = React.createContext();
// Gives the close + re-initialize cycle time to finish before allowing another
const RECLAIM_COOLDOWN_MS = 3000;

export function useNuanceContext() {
  return useContext(NuanceContext);
};

export default function NuanceWrapper({ children }) {

  const [nuanceState, setNuanceState] = useState('ACTIVE');
  
  const { user, practiceId } = useSelector(
    ({ auth }) => ({
      user: auth?.user,
      practiceId: auth?.practice_id,
    }),
    shallowEqual
  );
  const isBehavioralCareSolutions = practiceId == BEHAVIORAL_CARE_SOLUTIONS_ID;
  // Session-maintenance fix is rolled out to Behavioral Care Solutions only for now;
  // mirrored in a ref because setNuanceStateFromSdk is captured once by loadNuanceScript
  const isSessionFixEnabledRef = useRef(false);
  isSessionFixEnabledRef.current = isBehavioralCareSolutions;
  // Tracks that we intentionally closed the Nuance session (e.g. eSync flow),
  // so late SDK callbacks can't flip nuanceState back and skip re-initialization
  const isSessionClosedRef = useRef(false);
  // Tracks that another tab's SDK took over the Dragon session (one session per user),
  // which kills this tab's session on the server without any local notification
  const isSessionTakenOverRef = useRef(false);
  const isReclaimingSessionRef = useRef(false);
  const nuanceChannelRef = useRef(null);
  const nuanceTabIdRef = useRef(`nuance-tab-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const setNuanceStateFromSdk = (state) => {
    if (isSessionFixEnabledRef.current && isSessionClosedRef.current) return;
    setNuanceState(state);
  };

  const initializeNuanceInstance = () => {
    if (typeof window?.NUSA_initializeVuiForm !='undefined' && !window?.NUSA_initializeVuiForm()) {
      const promise = Promise.resolve(window?.NUSA_reinitializeVuiForm());
      promise.then(() => {
        setNuanceState('ACTIVE');
      });
    }
  }

  const isNuanceEnabled = () => {
    // Return boolean `true` if Nuance is already enabled, otherwise `false`
    return (
      document.getElementById('NUSAI_CommandBar_Container') &&
      document.getElementById('NUSAI_CommandBar_Container').style.visibility == "visible"
    );
  }

  const isNuanceRecording = () => {
    return (document.getElementById('NUSAI_CommandBar_Container') && 
            document.getElementById('NUSAI_CommandBar_Container').querySelectorAll('.NUSAI_CommandBar_Record_On').length > 0);
  }

  const startNuanceRecording = (inputID) => {
    if (isSessionTakenOverRef.current) {
      // Session was killed by another tab; re-establish it instead of feeding a dead session
      reclaimTakenOverSession();
      return;
    }
    if (isNuanceEnabled()) {
      if (!isNuanceRecording()) {
        window?.NUSA_startRecording();
        setNuanceState('RECORDING');
      }
      if (inputID) {
        if (document?.getElementById(inputID)?.className?.indexOf('ui-select-style') > -1) {
          document?.getElementById(inputID)?.querySelector('input[aria-autocomplete="list"]')?.focus();
        } else {
          document?.getElementById(inputID)?.focus();
        }
      }
    }
  }

  const initializeNuance = () => {
    isSessionClosedRef.current = false;
    setNuanceState('ACTIVE');
    initializeNuanceInstance();
  }

  const reinitializeNuance = () => {
    setTimeout(() => {
      if (window?.document.querySelectorAll("textarea[data-nusa-enabled]:not([data-nusa-id])").length > 0) {
        if (window?.NUSA_reinitializeVuiForm) {
          window?.NUSA_reinitializeVuiForm();
        }
      }
    }, 1000);
  }

  const stopNuanceRecording = () => {
    setNuanceStateFromSdk('ACTIVE');
    window?.NUSA_stopRecording && window?.NUSA_stopRecording();
  }

  const closeNuance = () => {
    isSessionClosedRef.current = true;
    setNuanceState('INACTIVE');
    window?.NUSA_closeVuiForm && window?.NUSA_closeVuiForm();
  }

  const announceSessionClaim = () => {
    nuanceChannelRef.current?.postMessage({ type: "SESSION_CLAIMED", tabId: nuanceTabIdRef.current });
  };

  const reclaimTakenOverSession = () => {
    // Recovery takes a moment; don't stack another one if focus bounces
    if (isReclaimingSessionRef.current) return;
    isReclaimingSessionRef.current = true;
    setTimeout(() => {
      isReclaimingSessionRef.current = false;
    }, RECLAIM_COOLDOWN_MS);

    isSessionTakenOverRef.current = false;
    announceSessionClaim();
    // The SDK can't be loaded twice on one page (its second load throws
    // "Identifier ... has already been declared"), so recover with the SDK's own
    // reset pair instead: close the dead VuiForm, then re-initialize it
    try {
      window?.NUSA_closeVuiForm && window.NUSA_closeVuiForm();
    } catch (err) {
      console.warn(err);
    }
    setTimeout(() => {
      initializeNuance();
    }, 500);
  };

  const recoverNuanceSession = () => {
    if (isSessionTakenOverRef.current) {
      reclaimTakenOverSession();
    } else if (isSessionClosedRef.current) {
      initializeNuance();
    }
  };

  const defaultValues = {
    nuanceState,
    setNuanceState,
    initializeNuance,
    startNuanceRecording,
    stopNuanceRecording,
    isNuanceRecording,
    reinitializeNuance,
    isNuanceEnabled,
    closeNuance,
  };

  useEffect(() => {
    if (user) {
      loadNuanceScript(user, setNuanceStateFromSdk, isNuanceEnabled);
    }
  }, [user]);

  // Providers keep multiple tabs open; when they return to a tab whose session
  // was closed (eSync flow) or taken over by another tab, re-establish it
  useEffect(() => {
    if (!isBehavioralCareSolutions) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recoverNuanceSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', recoverNuanceSession);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', recoverNuanceSession);
    };
  }, [isBehavioralCareSolutions]);

  // Cross-tab coordination: loading the Dragon SDK in a new tab takes over the
  // user's single server session, silently killing the other tabs' sessions.
  // Each tab announces its takeover so the others know to recover on next focus.
  useEffect(() => {
    if (!isBehavioralCareSolutions || typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel("ethizo-nuance-session");
    nuanceChannelRef.current = channel;
    channel.onmessage = (event) => {
      if (event.data?.type === "SESSION_CLAIMED" && event.data?.tabId !== nuanceTabIdRef.current) {
        isSessionTakenOverRef.current = true;
      }
    };
    // The SDK loads ~7s after startup (see reloadNuanceScripts timers), which is
    // when this tab actually takes over the session — announce after that point
    const announceTimer = setTimeout(announceSessionClaim, 8000);

    return () => {
      clearTimeout(announceTimer);
      nuanceChannelRef.current = null;
      channel.close();
    };
  }, [isBehavioralCareSolutions]);



  return (
    <NuanceContext.Provider value={defaultValues}>
      {children}
    </NuanceContext.Provider>
  );
};
